import { Box, SelectChangeEvent, SxProps, Theme, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { phrasesMessages } from '../../translations/phrases.messages';
import { HBPagination } from '../HBPagination/HBPagination';
import { HBSelect } from '../HBSelect/HBSelect';
import { gridClasses } from './HBAgGrid.styles';

export type AgGridPaginationOptions = {
  hideGoto?: boolean;
  hideselectPage?: boolean;
};

export type AgGridPaginationProps = {
  totalRows: number;
  pageSize: number;
  pageNumber?: number;
  onChange?: (page: number) => void;
  onChangePageSize?: (pageSize: number) => void;
  onChangeClient?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onChangePageSizeClient?: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  totalRowsLabel?: string;
  gridRef?: React.RefObject<AgGridReact>;
  options?: AgGridPaginationOptions;
  wrapperSx?: SxProps<Theme>;
  siblingCount?: number;
  showFirstButton?: boolean;
  showLastButton?: boolean;
};

const Pagination = ({
  totalRows,
  pageSize,
  pageNumber,
  onChange,
  onChangePageSize,
  onChangeClient,
  onChangePageSizeClient,
  totalRowsLabel,
  gridRef,
  options = { hideselectPage: false, hideGoto: false },
  siblingCount,
  showFirstButton,
  showLastButton,
}: AgGridPaginationProps): JSX.Element => {
  const [totalPage, setTotalPage] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(pageNumber || 1);
  const [pageSize1, setPageSize1] = useState<number>(pageSize);
  const { formatMessage } = useIntl();
  const start = currentPage * pageSize1 - (pageSize1 - 1);
  const end = Math.min(start + pageSize1 - 1, totalRows);

  useEffect(() => {
    const numberOfPages = Math.floor((totalRows + pageSize1 - 1) / pageSize1);
    setTotalPage(numberOfPages);
  }, [totalRows, pageSize1]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, page: number): void => {
    setCurrentPage(page);
    onChange?.(page);
    onChangeClient?.(event, page);
  };

  const handleChangePageSize = (
    event: SelectChangeEvent<unknown>,
    child: React.ReactNode,
  ): void => {
    onChangePageSize?.(Number(event.target.value));
    onChangePageSizeClient?.(event, child);
    setPageSize1(Number(event.target.value));
  };

  const handleGotoPage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    onChange?.(pageNumber);
    const event: any = {};
    onChangeClient?.(event, pageNumber);

    gridRef?.current?.api.paginationGoToPage(pageNumber - 1);
  };
  useEffect(() => {
    if (pageSize) {
      setPageSize1(pageSize);
    }
  }, [pageSize]);

  return (
    <Box>
      <Box sx={gridClasses.gridPaginationWrapper} id="gridPaginationWrapper">
        <HBPagination
          count={totalPage}
          page={pageNumber || currentPage}
          onChange={handleChangePage}
          gotoPage={!options.hideGoto ? handleGotoPage : undefined}
          {...{ siblingCount, showFirstButton, showLastButton }}
        />
        {!options.hideselectPage && (
          <Box sx={gridClasses.gridPaginationLabel}>
            <HBSelect
              sx={{ mr: 1, height: 30, minWidth: 107 }}
              label={totalRowsLabel || ''}
              size="small"
              onChange={handleChangePageSize}
              value={pageSize1}
              menuItem={['10', '25', '50', '100', '500', '1000', '100000', '1000000'].map(
                (value) => ({ title: value, value: +value }),
              )}
            />
            <Box sx={{ ...gridClasses.gridPaginationTotal }}>
              <Box>
                <Typography variant="subtitle2" component="span">
                  {formatMessage(phrasesMessages.from)} {totalRows}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" component="span">
                  {end}
                </Typography>{' '}
                -
                <Typography variant="subtitle2" component="span">
                  {start}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Pagination;
